import type { ReactNode } from 'react';
import { FOLLOWER_TIERS, type DiscoveryFilters, type CreatorTier } from '@/hooks/useDiscovery';
import MarketSelect from '@/components/base/MarketSelect';
import Button from '@/components/base/Button';
import PlatformIcon from '@/components/base/PlatformIcon';

interface FilterRailProps {
  filters: DiscoveryFilters;
  /** Category and platform names from `GET /discovery/options`. */
  categories: string[];
  platforms: string[];
  onToggleCategory: (value: string) => void;
  onSetMarkets: (value: string[]) => void;
  onTogglePlatform: (value: string) => void;
  onToggleTier: (value: CreatorTier) => void;
  onMinEngagement: (value: number) => void;
  onClearAll: () => void;
  onSaveSearch: () => void;
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="border-b border-background-100 px-4 py-3.5 last:border-0">
      <p className="mb-2 text-[11px] font-label font-semibold uppercase tracking-wider text-foreground-400">
        {title}
      </p>
      {children}
    </div>
  );
}

function CheckRow({
  label,
  hint,
  checked,
  onToggle,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center gap-2 rounded-sm py-1 text-left cursor-pointer group"
    >
      <span
        className={`w-4 h-4 shrink-0 flex items-center justify-center rounded-sm border text-[11px] ${
          checked
            ? 'border-primary-500 bg-primary-500 text-background-50'
            : 'border-background-300 text-transparent group-hover:border-primary-300'
        }`}
      >
        <i className="ri-check-line" />
      </span>
      <span className="flex-1 text-xs text-foreground-700">{label}</span>
      {hint ? <span className="text-[11px] text-foreground-400">{hint}</span> : null}
    </button>
  );
}

export default function FilterRail({
  filters,
  categories,
  platforms,
  onToggleCategory,
  onSetMarkets,
  onTogglePlatform,
  onToggleTier,
  onMinEngagement,
  onClearAll,
  onSaveSearch,
}: FilterRailProps) {
  const active =
    filters.categories.length + filters.markets.length + filters.platforms.length + filters.tiers.length + (filters.minEngagement > 0 ? 1 : 0);
  return (
    <div className="flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 lg:max-h-[calc(100vh-6.5rem)]">
      <div className="flex shrink-0 items-center justify-between border-b border-background-200 px-4 py-3">
        <span className="inline-flex items-center gap-1.5 text-sm font-heading font-semibold text-foreground-950">
          <i className="ri-filter-3-line text-base text-foreground-500" />
          Filters
          {active > 0 ? (
            <span key={active} className="animate-pop rounded-full bg-primary-500 px-1.5 text-[11px] font-bold leading-[18px] text-background-50">
              {active}
            </span>
          ) : null}
        </span>
        <button
          type="button"
          onClick={onClearAll}
          className="text-xs font-medium text-primary-700 cursor-pointer hover:text-primary-800"
        >
          Clear all
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <Section title="Category">
          <div className="pr-1">
            {categories.map((category) => (
              <CheckRow
                key={category}
                label={category}
                checked={filters.categories.includes(category)}
                onToggle={() => onToggleCategory(category)}
              />
            ))}
          </div>
        </Section>

        <Section title="Market">
          <MarketSelect
            value={filters.markets}
            onChange={onSetMarkets}
            placeholder="Search African countries…"
          />
        </Section>

        <Section title="Platform">
          <div className="flex flex-wrap gap-1.5">
            {platforms.map((platform) => {
              const active = filters.platforms.includes(platform);
              return (
                <button
                  key={platform}
                  type="button"
                  onClick={() => onTogglePlatform(platform)}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-label cursor-pointer transition-colors ${
                    active
                      ? 'border-primary-500 bg-primary-500 text-background-50'
                      : 'border-background-300 text-foreground-600 hover:border-primary-300'
                  }`}
                >
                  <PlatformIcon platform={platform} className="text-[13px]" mono={active} />
                  {platform}
                </button>
              );
            })}
          </div>
        </Section>

        <Section title="Follower tier">
          <div className="space-y-0.5">
            {FOLLOWER_TIERS.map((tier) => (
              <CheckRow
                key={tier.id}
                label={tier.label}
                hint={tier.hint}
                checked={filters.tiers.includes(tier.id as CreatorTier)}
                onToggle={() => onToggleTier(tier.id as CreatorTier)}
              />
            ))}
          </div>
        </Section>

        <Section title="Minimum engagement">
          <div className="flex items-center justify-between text-xs text-foreground-600">
            <span>At least</span>
            <span className="font-label font-medium text-foreground-900">
              {filters.minEngagement.toFixed(1)}%
            </span>
          </div>
          <input
            type="range"
            min={0}
            max={10}
            step={0.5}
            value={filters.minEngagement}
            onChange={(e) => onMinEngagement(Number(e.target.value))}
            className="mt-2 w-full accent-primary-500 cursor-pointer"
          />
        </Section>
      </div>

      <div className="p-4">
        <Button
          variant="outline"
          size="sm"
          icon="ri-bookmark-line"
          className="w-full"
          onClick={onSaveSearch}
        >
          Save this search
        </Button>
      </div>
    </div>
  );
}